/*
 * $Id:: utils.js 2021/12/13 12:44 otsolka
 *
 *        . * .
 *      * RRRR  *   Copyright (c) 2012-2021 EUIPO: European Intelectual
 *     .  RR  R  .  Property Organization (trademarks and designs).
 *     *  RRR    *
 *      . RR RR .   ALL RIGHTS RESERVED
 *       *. _ .*
 *
 *  The use and distribution of this software is under the restrictions exposed in 'license.txt'
 */

import React from 'react';

import Checkbox from '@material-ui/core/Checkbox';
import IconButton from '@material-ui/core/IconButton';
import Tooltip from '@material-ui/core/Tooltip';
import { Link } from 'react-router-dom';

import { ReactComponent as DownloadIcon } from '../../assets/images/download.svg';
import { ReactComponent as InfoErrorIcon } from '../../assets/images/info-panel-open.svg';
import { ReactComponent as PaperClipIcon } from '../../assets/images/paperClip.svg';
import { ReactComponent as ReplyIcon } from '../../assets/images/reply.svg';
import { ReactComponent as UnreadIcon } from '../../assets/images/unread.svg';
import { ReactComponent as TrashIcon } from '../../assets/images/trash.svg';
import { ReactComponent as WarningFilledIcon } from '../../assets/images/warning_filled.svg';
import { ReactComponent as WarningOutlinedIcon } from '../../assets/images/warning_outlined.svg';
import { t, bareDateTimeFormat, isDateReached } from '../../providers';

const labels = { NA: 'N/A' };
const statuses = { NEW: 'NEW', DRAFT: 'DRAFT', READ: 'READ', SENT: 'SENT' };

/**
 * format status
 * @param {*} status
 * @returns highlighted status
 */
const formatColor = (status) => {
    let toReturn;
    if (status === statuses.NEW) {
        toReturn = (
            <span
                style={{
                    color: '#135FA5',
                    border: '1px solid #135FA5',
                    borderRadius: '2px',
                    padding: '1px 5px',
                    fontSize: '10px',
                    fontWeight: '600',
                    backgroundColor: '#ffffff',
                }}>
                {(t('correspondence.status.' + status)).toUpperCase()}
            </span>
        );
    } else if (status === statuses.DRAFT) {
        toReturn = (
            <span
                style={{
                    color: '#303233',
                    border: '1px solid #707070',
                    borderRadius: '2px',
                    padding: '1px 5px',
                    fontSize: '10px',
                    fontWeight: '600',
                }}>
                {(t('correspondence.status.' + status)).toUpperCase()}
            </span>
        );
    } else if (status === statuses.READ || status === statuses.SENT) {
        toReturn = (
            <span
                style={{
                    color: '#006A00',
                    border: '1px solid #1CA73D',
                    borderRadius: '2px',
                    padding: '1px 5px',
                    fontSize: '10px',
                    fontWeight: '600',
                }}>
                {(t('correspondence.status.' + status)).toUpperCase()}
            </span>
        );
    }
    return toReturn;
};

/**
 * format date depending on today
 * @param {boolean} requiresReply
 * @param {string} date
 * @param {boolean} hasReplies
 * @returns formatted date
 */
const dueDateParser = (requiresReply, date, hasReplies = false) => {
    const formattedDate = bareDateTimeFormat(date, 'YYYY-mm-dd');
    const dateReached = isDateReached(date);
    let toReturn;
    if (requiresReply) {
        if (hasReplies) {
            toReturn = (
                <div>
                    <ReplyIcon style={{ marginRight: '4px', verticalAlign: 'top', fill: '#1CA73D' }} /> {formattedDate}
                </div>
            );
        } else {
            if (dateReached) {
                toReturn = (
                    <div style={{ color: '#BE0921' }}>
                        <InfoErrorIcon style={{ marginRight: '4px', verticalAlign: 'top' }} /> {formattedDate}
                    </div>
                );
            } else {
                toReturn = (
                    <div style={{ color: '#945900' }}>
                        <WarningFilledIcon style={{ marginRight: '4px', verticalAlign: 'top' }} /> {formattedDate}
                    </div>
                );
            }
        }
    } else {
        toReturn = (
            <div style={{ color: '#00294E' }}>
                <WarningOutlinedIcon style={{ marginRight: '4px', verticalAlign: 'top' }} /> {formattedDate}
            </div>
        );
    }
    return toReturn;
};

/**
 * format date typpe with icon
 * @param {string} type
 * @param {string} date
 * @returns formatted date type with icon
 */
const receiveSentDateParser = (type, date) => {
    let toReturn;
    if (type === 'received') {
        toReturn = (
            <div>
                {/*
                <Tooltip arrow placement={'top'} title={t('received.date')}>
                    <span
                        className={'receivedDateIcon'}
                        style={{
                            color: '#707070',
                            border: '1px solid #9B9B9B',
                            borderRadius: '2px',
                            padding: '1px 5px',
                            fontSize: '10px',
                            fontWeight: '600',
                            marginRight: '4px',
                            height: '15px',
                            verticalAlign: 'text-bottom',
                        }}>

                    </span>
                </Tooltip>
                */}
                {date}
            </div>
        );
    } else if (type === 'sent') {
        toReturn = (
            <div>
                {/*
                <Tooltip arrow placement={'top'} title={t('sent.date')}>
                    <span
                        style={{
                            color: '#707070',
                            border: '1px solid #9B9B9B',
                            borderRadius: '2px',
                            padding: '1px 5px',
                            fontSize: '10px',
                            fontWeight: '600',
                            marginRight: '4px',
                            verticalAlign: 'text-bottom',
                        }}>
                        S
                    </span>
                </Tooltip>
                */}
                {date}
            </div>
        );
    }
    return toReturn;
};

/**
 * check reply ability of cor/nce
 * @param {string} dueDate
 * @param {boolean} requiresReply
 * @param {array} replies
 * @param {number} maxRepliesAllowed
 * @param {boolean} readOnly
 * @returns info for reply cor/nce ability
 */
const canReply = (dueDate, requiresReply, replies, maxRepliesAllowed = 5, readOnly) =>
    !readOnly /* && requiresReply && !isDateReached(dueDate) && replies && replies.length < maxRepliesAllowed */;

/**
 * category with optional attachment icon
 * @param {string} category
 * @param {bolean} hasAttachment
 * @returns category with optional attachment icon
 */
const categoryHasAttachments = (category, hasAttachment) => (
    <span style={{ display: 'flex', alignItems: 'center', minWidth: '75px' }}>
        {t('correspondence.category.' + category)} {!!hasAttachment ? <PaperClipIcon style={{ paddingLeft: '8px' }} /> : ''}
    </span>
);

/**
 * application Number navigates to myApplications page
 * @param {string} applicationNumber
 * @param {string} applicationType
 * @returns application Number as link to navigate to myApplications page
 */
const handleAppNumber = (applicationNumber, applicationType) =>
    {
        //tab name in my-applications
        let tabType = ('TrademarkApplication' === applicationType)
            ? 'trademark'
            : ('DesignApplication' === applicationType)
                ? 'design'
                : 'eservice'
        return applicationType ? (
            <Link
                to={{
                    pathname: '/my-applications',
                    state: { appNumber: applicationNumber, type: tabType },
                }}>
                {applicationNumber}
            </Link>
        ) : (
            applicationNumber
        )
    };

/**
 * value with tooltip
 * @param {string} text
 * @returns value with tooltip
 */
const valueWithTooltip = (text) => (
    <Tooltip arrow title={text} placement={'left'}>
        <span>{text}</span>
    </Tooltip>
);

/**
 * corr/nce data parser for grid display
 * @param {object} correspondence
 * @param {function} handleNavigation
 * @param {function} handleDeleteDraft
 * @param {function} handleMarkAsUnread
 * @param {boolean} readOnly
 * @param {number} maxReplies
 * @param {function} downloadIncoming
 * @param {function} downloadOutgoing
 */
const correspondenceParserGrid = (
    correspondence,
    handleNavigation,
    handleDeleteDraft,
    handleMarkAsUnread,
    readOnly,
    maxReplies,
    downloadIncoming,
    downloadOutgoing
    ) => {
    const { content } = correspondence;

    const sortableFields = [
        { label: t('category'), value: 'CATEGORY' },
        { label: t('subject'), value: 'SUBJECT' },
        { label: t('application.number'), value: 'APPLICATION_NUMBER' },
        { label: t('due.date'), value: 'DUE_DATE' },
        { label: t('received.date'), value: 'RECEIVED_DATE' },
        { label: t('sent.date'), value: 'SENT_DATE' },
        { label: t('status.correspondence'), value: 'STATUS' },
    ];

    let subrows = [];
    let toPush = [];
    const correspondenceGrid = (content || []).map(({ message, subRows }) => {
        return {
            details: [
                { label: t('correspondence.number'), value: message.boCorrespondenceNumber ? message.boCorrespondenceNumber : '' },
                { label: t('status.correspondence'), code: 'status', value: message.messageStatus ? formatColor(message.messageStatus) : '' },
                {
                    label: t('category'),
                    value: message.dossierType ? categoryHasAttachments(message.dossierType, message.hasAttachment) : '',
                },
                {
                    label: t('subject'),
                    value: message.subject ? message.subject : '',
                },
                {
                    label: t('application.number'),
                    value: message.applicationNumber ? handleAppNumber(message.applicationNumber, message.dossierType) : '',
                },
                {
                    label: t('application.registration.number'),
                    value: message.registrationNumber,
                },
                {
                    label: t('received'),
                    value: message.receivedDate ? message.receivedDate && bareDateTimeFormat(message.receivedDate) : '',
                },
                {
                    label: t('due'),
                    value: (message.dueDate && dueDateParser(message.requiresReply, message.dueDate, message.hasReply)) || '',
                },
                (subrows = []),
                !!subRows.length &&
                    subRows.map((subrow) => {
                        toPush = [];

                        toPush = [
                            {
                                label: t('correspondence.number'),
                                value: subrow.boCorrespondenceNumber ? subrow.boCorrespondenceNumber : '',
                            },
                            {
                                label: t('status.correspondence'),
                                code: 'status',
                                value: subrow.draftStatus ? formatColor(subrow.draftStatus) : '',
                            },
                            {
                                label: t('category'),
                                value: message.dossierType ? categoryHasAttachments(message.dossierType, subrow.hasAttachment) : '',
                            },
                            {
                                label: t('subject'),
                                value: message.subject ? message.subject : '',
                            },
                            {
                                label: t('application.number'),
                                value: message.applicationNumber ? handleAppNumber(message.applicationNumber, message.dossierType) : '',
                            },
                            {
                                label: t('application.registration.number'),
                                value: message.registrationNumber,
                            },
                            subrow.draftStatus === statuses.SENT && {
                                label: t('sent'),
                                value: subrow.actionDate ? subrow.actionDate && bareDateTimeFormat(subrow.actionDate) : '',
                            },
                            {
                                label: 'menuItems',
                                menuItems: [
                                    {
                                        label: t('download'),
                                        action: () => downloadOutgoing(subrow.id),
                                    },
                                    subrow.draftStatus === statuses.DRAFT && !readOnly && { label: t('delete'), action: () => handleDeleteDraft(subrow.id) },
                                    {
                                        label: t('open.correspondence'),
                                        action: () =>
                                            handleNavigation({
                                                pathname: `/correspondence/${message.id}`,
                                                state: {
                                                    threadId: message.id,
                                                    openReply: subrow.id,
                                                },
                                            }),
                                    },
                                ].filter((item) => typeof item === 'object'),
                            },
                        ].filter((item) => typeof item === 'object');
                        subrows.push(toPush);
                    }),
                { label: 'subrows', subrows: subrows || [] },
            ].filter((item) => typeof item === 'object'),
            isNew: message.messageStatus === statuses.NEW,
            isDraft: message.draftStatus === statuses.DRAFT,
            menuItems: [
                { label: t('download'), action: () => downloadIncoming(message.id) },
                (message.direction !== 'BO_INCOMING') && canReply(message.dueDate, message.requiresReply, subRows, maxReplies, readOnly) && {
                    label: t('reply'),
                    action: () =>
                        handleNavigation({
                            pathname: `/correspondence/${message.id}`,
                            state: { threadId: message.id, initiateReply: true },
                        }),
                },
                {
                    label: t('open.correspondence'),
                    action: () =>
                        handleNavigation({
                            pathname: `/correspondence/${message.id}`,
                            state: {
                                threadId: message.id,
                            },
                        }),
                },
                {
                    label: t('mark.as.unread1'),
                    action: () => handleMarkAsUnread(message),
                }
            ].filter((item) => typeof item === 'object'),
        };
    });
    return { correspondenceGrid, sortableFields };
};

/**
 * corr/nce data parser for list display
 * @param {object} correspondence
 * @param {function} handleNavigation
 * @param {function} handleDeleteDraft
 * @param {function} handleToggleImportant
 * @param {function} handleMarkAsUnread
 * @param {boolean} readOnly
 * @param {number} maxReplies
 * @param {function} downloadIncoming
 * @param {function} downloadOutgoing
 * @param {boolean} enableSorting
 */
const correspondenceParserList = (
    correspondence,
    handleNavigation,
    handleDeleteDraft,
    handleToggleImportant,
    handleMarkAsUnread,
    readOnly,
    maxReplies,
    downloadIncoming,
    downloadOutgoing,
    enableSorting = false
) => {
    const { content } = correspondence;

    let correspondenceViewList = {
        headers: [
            { label: t('correspondence.important'), code: 'IMPORTANT', align: 'left', sortable: enableSorting },
            { label: t('correspondence.number'), code: 'number', align: 'left', sortable: false },
            { label: t('category'), code: 'CATEGORY', align: 'left', sortable: enableSorting },
            { label: t('subject'), code: 'SUBJECT', align: 'left', sortable: enableSorting },
            { label: t('application.number'), code: 'APPLICATION_NUMBER', align: 'left', sortable: enableSorting },
            { label: t('application.registration.number'), code: 'REGISTRATION_NUMBER', align: 'left', sortable: enableSorting },
            { label: t('received.sent.date'), code: 'RECEIVED_DATE', align: 'left', sortable: enableSorting },
            { label: t('status.correspondence'), code: 'STATUS', align: 'left', sortable: enableSorting },
            { label: '', code: 'actions', align: 'left', sortable: false },
        ].filter((item) => typeof item === 'object'), //remove non-objects from headers
    };

    let dataList = [];
    let subrows = [];
    let toPush = [];

    // table/list data
    const parsedCorr = (content || []).map(({ message, subRows }, idx) => [
        {
            label: 'correspondence.important',
            value: <Checkbox
                        checked={!!message.important}
                        id={ message.id + '-important'}
                        onChange={(event)=>{ handleToggleImportant(event, message);}}
                    />,
            blueBackground: message.messageStatus === statuses.NEW ? true : false,
        },
        { label: 'correspondence.number', value: message.boCorrespondenceNumber ? message.boCorrespondenceNumber : '' },
        { label: 'category', value: message.dossierType ? categoryHasAttachments(message.dossierType, message.hasAttachment) : '' },
        { label: 'subject', value: message.subject ? valueWithTooltip(message.subject) : '' },
        { label: 'application.number', value: message.applicationNumber ? handleAppNumber(message.applicationNumber, message.dossierType) : '' },
        { label: 'application.registration.number', value: message.registrationNumber },
        { label: 'received.sent.date', value: message.receivedDate ? receiveSentDateParser('received', bareDateTimeFormat(message.receivedDate)) : '' },
        { label: 'status.correspondence', value: message.messageStatus ? formatColor(message.messageStatus) : '' },
        {
            label: 'actions',
            value: (
                <div>
                    {/*
                    <IconButton onClick={() => downloadIncoming(message.id)}>
                        <Tooltip arrow placement={'top'} title={t('download')}>
                            <DownloadIcon style={{ marginRight: '31px' }} />
                        </Tooltip>
                    </IconButton>
                    */}
                    {(message.direction !== 'BO_INCOMING') && canReply(message.dueDate, message.requiresReply, subRows, maxReplies) ? (
                        <IconButton
                            disabled={readOnly}
                            onClick={() =>
                                handleNavigation({
                                    pathname: `/correspondence/${message.id}`,
                                    state: {
                                        threadId: message.id,
                                        initiateReply: true,
                                    },
                                })
                            }>
                            <Tooltip arrow placement={'top'} title={t('reply')}>
                                <ReplyIcon style={{ marginRight: '31px' }} />
                            </Tooltip>
                        </IconButton>
                    ) : (
                        <span style={{ marginRight: '48px' }}> </span>
                    )}
                    {(message.messageStatus === statuses.READ) ? (
                        <IconButton
                            disabled={message.messageStatus === statuses.NEW}
                            onClick={() => handleMarkAsUnread(message)}
                            >
                            <Tooltip arrow placement={'top'} title={t('mark.as.unread')}>
                                <UnreadIcon style={{ marginRight: '31px', maxWidth: '16px', maxHeight: '16px' }} />
                            </Tooltip>
                        </IconButton>
                    ) : (<span style={{ marginRight: '48px' }}> </span>)}
                    <Link
                        className={'openButton'}
                        to={{
                            pathname: `/correspondence/${message.id}`,
                            state: {
                                threadId: message.id,
                            },
                        }}>
                        {t('open.correspondence')}
                    </Link>
                </div>
            ),
        },
        (subrows = []),
        subRows &&
            subRows.map((subrow) => {
                toPush = [];

                toPush = [
                    { label: '', value: '' },//simulate empty data matching parent-row's checkbox-column to have same alignment when expanded
                    { label: 'correspondence.number', value: subrow.boCorrespondenceNumber ? subrow.boCorrespondenceNumber : '' },
                    { label: 'category', value: message.dossierType ? categoryHasAttachments(message.dossierType, subrow.hasAttachment) : '' },
                    { label: 'subject', value: message.subject ? valueWithTooltip(message.subject) : '' },
                    { label: 'application.number', value: message.applicationNumber ? handleAppNumber(message.applicationNumber, message.dossierType) : '' },
                    { label: 'application.registration.number', value: message.registrationNumber },
                    { label: 'received.sent.date',
                        value:
                            subrow.draftStatus === statuses.SENT
                                ? subrow.actionDate
                                    ? receiveSentDateParser('sent', bareDateTimeFormat(subrow.actionDate))
                                    : ''
                                : '',
                    },
                    { label: 'status.correspondence', value: subrow.draftStatus ? formatColor(subrow.draftStatus) : '' },
                    {
                        label: '',
                        value: (
                            <div>
                                {/*
                                <IconButton onClick={() => downloadOutgoing(subrow.id)}>
                                    <Tooltip arrow placement={'top'} title={t('download')}>
                                        <DownloadIcon style={{ marginRight: '14px' }} />
                                    </Tooltip>
                                </IconButton>
                                */}
                                {subrow.draftStatus === statuses.DRAFT ? (
                                    <IconButton disabled={readOnly} onClick={() => handleDeleteDraft(subrow.id)}>
                                        <Tooltip arrow placement={'top'} title={t('delete')}>
                                            <TrashIcon style={{ marginRight: '31px' }} />
                                        </Tooltip>
                                    </IconButton>
                                ) : (
                                    <span style={{ marginRight: '28px' }}> </span>
                                )}
                                <Link
                                    className={'openButton'}
                                    to={{
                                        pathname: `/correspondence/${message.id}`,
                                        state: {
                                            threadId: message.id,
                                            openReply: subrow.id,
                                        },
                                    }}>
                                    {t('open.correspondence')}
                                </Link>
                            </div>
                        ),
                    },
                    { label: '', value: '' },//simulate empty data matching parent-row's actions-column to have same alignment when expanded
                ];
                subrows.push(toPush);
            }),
        { label: 'subrows', subrows: subrows },
    ]);

    //remove non-objects from data
    dataList = parsedCorr.map((corr) => {
        corr.splice(9, 2);
        return corr.filter((item) => typeof item === 'object');
    });

    correspondenceViewList = {
        ...correspondenceViewList,
        data: dataList,
    };

    return correspondenceViewList;
};

/**
 * filters parser
 * @param {object} filters
 * @returns parsed filters
 */
const filtersParser = (filters) => {
    let parsedFilters = { ...filters };

    // Normalize date ranges to API accepted date range data type, if any.
    if (parsedFilters.dates) {
        const startDateTime = 'T00:00:00';
        const endDateTime = 'T23:59:59';
        parsedFilters = {
            ...parsedFilters,
            dates: parsedFilters.dates.reduce((allDates, { dateType, startDate, endDate }) => {
                if (!!dateType && !!startDate && !!endDate) {
                    allDates.push({
                        date: dateType,
                        dateFrom: `${bareDateTimeFormat(startDate, 'YYYY-mm-dd')}${startDateTime}`,
                        dateTo: `${bareDateTimeFormat(endDate, 'YYYY-mm-dd')}${endDateTime}`,
                    });
                }
                return allDates;
            }, []),
        };
    }

    // handle statuses
    if (parsedFilters.statuses && parsedFilters.statuses.length > 0) {
        const incomingStatuses = [statuses.NEW, statuses.READ];
        const outgoingStatuses = [statuses.SENT, statuses.DRAFT];

        let messageStatuses = [],
            draftStatuses = [];

        filters.statuses.forEach((status) => {
            if (incomingStatuses.indexOf(status) > -1) {
                messageStatuses.push(status);
            } else if (outgoingStatuses.indexOf(status) > -1) {
                draftStatuses.push(status);
            }
        });

        if (!!messageStatuses.length) parsedFilters = { ...parsedFilters, messageStatuses };
        if (!!draftStatuses.length) parsedFilters = { ...parsedFilters, draftStatuses };
        delete parsedFilters.statuses;
    }

    return parsedFilters;
};

/**
 * data parser
 * @param {object} data
 * @returns parsed data
 */
const parseRequestData = (data) => {
    let parameters = { ...data, filterCriteria: filtersParser(data.filters) };
    if (data.searchQuery) {
        parameters = {
            ...parameters,
            search: data.searchQuery,
        };
    }
    const updatedParams = { ...parameters };
    delete updatedParams['searchQuery'];
    delete updatedParams['filters'];
    delete updatedParams['filtersChips'];
    delete updatedParams['selectedTab'];

    return {
        requestBody: updatedParams,
        selectedTab: data.selectedTab,
    };
};

export {
    formatColor,
    dueDateParser,
    receiveSentDateParser,
    canReply,
    correspondenceParserGrid,
    correspondenceParserList,
    filtersParser,
    parseRequestData,
};
